import { ThemeEvents } from '@theme/events';

// Get configuration from theme settings
function getThemeConfig() {
  const configScript = document.getElementById('free-gift-config');
  let themeConfig = {};

  if (configScript) {
    try {
      themeConfig = JSON.parse(configScript.textContent);
      console.log('📋 Free gift config loaded:', themeConfig);
    } catch (error) {
      console.error('❌ Error parsing free-gift-config:', error);
    }
  } else {
    console.warn('⚠️ free-gift-config script not found. Using fallback values.');
  }

  return themeConfig;
}

// Configuration
const THEME_CONFIG = getThemeConfig();
const CONFIG = {
  discountCode: 'K242MCTZRDE7',
  freeGiftVariantId: THEME_CONFIG.freeGiftVariantId ? Number(THEME_CONFIG.freeGiftVariantId) : null, // Convert to number, no fallback - must be set in theme settings
  excludedProductIds: THEME_CONFIG.excludedProductIds || ['7549346545814'], // Fallback to default gift card ID
  cartThreshold: 5000, // $50.00 in cents
  storageKey: 'klaviyo_subscriber_gift_eligible',
  cartStorageKey: 'free_gift_added_to_cart',
  eligibilityApiUrl: 'https://free-gift-validator-ashy.vercel.app/api/check-customer',
  shop: 'https://rebel-cheese.myshopify.com',
  // Retry configuration for API calls (to handle DB sync delays)
  maxRetries: 4, // Total attempts: initial + 4 retries = 5 attempts
  retryDelays: [5000, 10000, 15000, 20000], // Wait times: 5s, 10s, 15s, 20s (total ~50s)
};

// Validate configuration on load
if (!CONFIG.freeGiftVariantId) {
  console.error('❌ Free gift variant ID is not configured. Please set "free_gift_klaviyo_product" in theme settings.');
}

class FreeGiftManager {
  constructor() {
    this.isProcessing = false;
    this.lastGiftCheck = 0;
    this.cartCheckTimeout = null;
    this.init();
  }

  init() {
    this.setupKlaviyoListener();
    this.setupCartListener();
    this.checkInitialState();
  }

  // Watch for Klaviyo form submission
  setupKlaviyoListener() {
    window.addEventListener('klaviyoForms', (event) => {
      if (event.detail.type === 'submit') {
        console.log('📧 Klaviyo form submitted with email:', event.detail.metaData.$email);
        this.handleKlaviyoSubmission(event.detail.metaData.$email);
      }
    });
  }

  // Handle Klaviyo form submission
  async handleKlaviyoSubmission(email) {
    try {
      // Prevent multiple simultaneous submissions
      if (this.isProcessing) {
        console.log('⏸️ Klaviyo submission already processing, skipping...');
        return;
      }

      console.log('📧 Klaviyo form submitted - checking eligibility via API for:', email);

      if (!email) {
        console.error('No email provided to handleKlaviyoSubmission');
        return;
      }

      this.isProcessing = true;
      try {
        // Check eligibility via API before proceeding
        const isEligible = await this.checkEligibilityViaApi(email);
        if (!isEligible) {
          console.log('🚫 Customer not eligible for free gift based on API response');
          return;
        }

        console.log('✅ Customer is eligible for free gift - proceeding with gift process');
        this.persistSubscription();
        await this.applyDiscountCode();
        await this.checkGiftEligibility();
      } finally {
        this.isProcessing = false;
      }
    } catch (error) {
      console.error('Error handling Klaviyo submission:', error);
      this.isProcessing = false;
    }
  }

  // Check customer eligibility via external API (with smart retry logic)
  async checkEligibilityViaApi(email) {
    console.log(`🔄 Starting eligibility check for: ${email}`);

    for (let attempt = 0; attempt <= CONFIG.maxRetries; attempt++) {
      try {
        const isFirstAttempt = attempt === 0;
        const attemptNumber = attempt + 1;

        if (!isFirstAttempt) {
          console.log(`🔄 Retry attempt ${attempt}/${CONFIG.maxRetries} for: ${email}`);
        }

        const response = await fetch(
          `${CONFIG.eligibilityApiUrl}?shop=${CONFIG.shop}&email=${encodeURIComponent(email)}`
        );

        if (!response.ok) {
          console.error(
            `⚠️ API request failed with status: ${response.status} (Attempt ${attemptNumber}/${CONFIG.maxRetries + 1})`
          );

          // Only retry on network/server errors, not on successful responses
          if (attempt < CONFIG.maxRetries) {
            console.log(`⏰ Waiting ${CONFIG.retryDelays[attempt] / 1000}s before retry...`);
            await this.wait(CONFIG.retryDelays[attempt]);
            continue;
          }

          return false; // All retries exhausted
        }

        const data = await response.json();

        if (data.eligible === true) {
          // Customer is eligible - STOP IMMEDIATELY, no more retries needed
          console.log(
            `✅ API confirms customer IS ELIGIBLE (Attempt ${attemptNumber}/${CONFIG.maxRetries + 1}):`,
            data
          );
          console.log(`🎯 Eligibility confirmed! Stopping any further checks.`);
          return true;
        } else if (data.eligible === false) {
          // Customer not eligible - might be DB sync delay, so retry
          console.log(
            `⏳ API returned eligible:false (Attempt ${attemptNumber}/${CONFIG.maxRetries + 1}) - may be a DB sync delay`
          );

          // If not the last attempt, wait and retry
          if (attempt < CONFIG.maxRetries) {
            console.log(`⏰ Waiting ${CONFIG.retryDelays[attempt] / 1000}s before retry to allow DB sync...`);
            await this.wait(CONFIG.retryDelays[attempt]);
            continue; // Retry
          }

          // All retries exhausted and still not eligible
          console.log(`🚫 Final result: Customer is NOT ELIGIBLE after ${CONFIG.maxRetries + 1} attempts`);
          return false;
        } else {
          // Unexpected response format
          console.error(`⚠️ Unexpected API response format:`, data);
          return false; // Fail safe
        }
      } catch (error) {
        console.error(`❌ Error on attempt ${attemptNumber}/${CONFIG.maxRetries + 1}:`, error);

        // Retry on network/parsing errors
        if (attempt < CONFIG.maxRetries) {
          console.log(`⏰ Waiting ${CONFIG.retryDelays[attempt] / 1000}s before retry after error...`);
          await this.wait(CONFIG.retryDelays[attempt]);
          continue;
        }

        // All retries exhausted
        console.error('🚫 All retry attempts failed due to errors');
        return false; // Fail safe - don't give gift if API call fails
      }
    }

    return false; // Fallback (should never reach here)
  }

  // Helper function to wait/delay
  wait(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  // Persist subscription across devices
  persistSubscription() {
    const subscriptionData = {
      timestamp: Date.now(),
      discountCode: CONFIG.discountCode,
      eligible: true,
    };

    localStorage.setItem(CONFIG.storageKey, JSON.stringify(subscriptionData));
    sessionStorage.setItem(CONFIG.storageKey, JSON.stringify(subscriptionData));
  }

  // Apply discount code
  async applyDiscountCode() {
    try {
      document.cookie = `discount_code=${CONFIG.discountCode}; path=/; max-age=7200`;
      await fetch(`/discount/${CONFIG.discountCode}`);
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: {} }),
      });
    } catch (error) {
      console.error('Error applying discount code:', error);
    }
  }

  // Check if customer is eligible for free gift
  async checkGiftEligibility() {
    try {
      if (this.isProcessing) return;
      if (this.hasReceivedGift()) return;

      this.isProcessing = true;
      try {
        const cart = await this.getCurrentCart();
        // Double-check gift is not already in cart before adding (check for Klaviyo subscriber gift specifically)
        const hasGift = cart.items.some(
          (item) => item.variant_id === CONFIG.freeGiftVariantId && item.properties?._klaviyo_subscriber_gift === 'true'
        );
        if (hasGift) {
          this.markGiftAsReceived();
          return;
        }

        const cartSubtotal = this.calculateCartTotalExcludingGiftCards(cart);
        if (cartSubtotal >= CONFIG.cartThreshold) {
          await this.addFreeGift();
        }
      } finally {
        this.isProcessing = false;
      }
    } catch (error) {
      console.error('Error checking gift eligibility:', error);
      this.isProcessing = false;
    }
  }

  // Check if customer has already received the gift
  hasReceivedGift() {
    const giftData = localStorage.getItem(CONFIG.cartStorageKey);
    if (!giftData) return false;

    try {
      const data = JSON.parse(giftData);
      return data.received === true;
    } catch {
      return false;
    }
  }

  // Get current cart data
  async getCurrentCart() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      return cart;
    } catch (error) {
      console.error('Error fetching cart:', error);
      return { items: [], items_subtotal_price: 0 };
    }
  }

  // Add free gift to cart
  async addFreeGift() {
    try {
      // Validate variant ID is configured
      if (!CONFIG.freeGiftVariantId) {
        console.error('❌ Cannot add free gift: variant ID not configured');
        return;
      }

      // Check if gift is already in cart before adding (check for Klaviyo subscriber gift specifically)
      const cart = await this.getCurrentCart();
      const existingGift = cart.items.find(
        (item) => item.variant_id === CONFIG.freeGiftVariantId && item.properties?._klaviyo_subscriber_gift === 'true'
      );

      if (existingGift) {
        // Gift already exists - ensure quantity is 1 and mark as received
        if (existingGift.quantity !== 1) {
          // Fix quantity if it's not 1
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: existingGift.key,
              quantity: 1,
            }),
          });
        }
        this.markGiftAsReceived();
        await this.refreshCartUI();
        return;
      }

      // Check hasReceivedGift one more time before adding (double-check)
      if (this.hasReceivedGift()) {
        return;
      }

      // Validate variant ID before attempting to add
      if (!CONFIG.freeGiftVariantId || isNaN(CONFIG.freeGiftVariantId)) {
        console.error('❌ Invalid free gift variant ID:', CONFIG.freeGiftVariantId);
        throw new Error(`Invalid free gift variant ID: ${CONFIG.freeGiftVariantId}`);
      }

      console.log('🎁 Attempting to add free gift with variant ID:', CONFIG.freeGiftVariantId);

      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: CONFIG.freeGiftVariantId,
          quantity: 1,
          properties: {
            _free_gift: 'true',
            _klaviyo_subscriber_gift: 'true',
          },
        }),
      });

      if (!addResponse.ok) {
        // Try to get error details from response
        let errorMessage = `Failed to add gift: ${addResponse.status}`;
        try {
          const errorData = await addResponse.json();
          errorMessage = `Failed to add gift: ${addResponse.status} - ${errorData.description || errorData.message || JSON.stringify(errorData)}`;
          console.error('❌ Shopify API error response:', errorData);
        } catch (parseError) {
          const errorText = await addResponse.text();
          console.error('❌ Shopify API error response (text):', errorText);
          errorMessage = `Failed to add gift: ${addResponse.status} - ${errorText}`;
        }
        throw new Error(errorMessage);
      }

      const result = await addResponse.json();
      console.log('✅ Free gift added successfully:', result);

      this.markGiftAsReceived();
      await this.refreshCartUI();
    } catch (error) {
      console.error('Error adding free gift:', error);
    }
  }

  // Remove free gift from cart
  async removeFreeGift() {
    try {
      const cart = await this.getCurrentCart();
      // Only remove Klaviyo subscriber gift specifically
      const giftItem = cart.items.find(
        (item) => item.variant_id === CONFIG.freeGiftVariantId && item.properties?._klaviyo_subscriber_gift === 'true'
      );

      if (!giftItem) {
        // Gift not in cart, but clear the received flag anyway
        localStorage.removeItem(CONFIG.cartStorageKey);
        return;
      }

      const removeResponse = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: giftItem.key,
          quantity: 0,
        }),
      });

      if (!removeResponse.ok) {
        throw new Error(`Failed to remove gift: ${removeResponse.status}`);
      }

      // Clear the received flag when gift is removed
      localStorage.removeItem(CONFIG.cartStorageKey);
      await this.refreshCartUI();
    } catch (error) {
      console.error('Error removing free gift:', error);
    }
  }

  // Mark gift as received
  markGiftAsReceived() {
    const giftData = {
      received: true,
      timestamp: Date.now(),
      variantId: CONFIG.freeGiftVariantId,
    };

    localStorage.setItem(CONFIG.cartStorageKey, JSON.stringify(giftData));
  }

  // Refresh cart UI
  async refreshCartUI() {
    try {
      const CartModule = await import('@theme/cart-store');
      // Try both default and named export
      const cartStore = CartModule.default || CartModule.Cart;
      if (cartStore) {
        // Set internal refresh flag so the event is dispatched
        cartStore._isInternalRefresh = true;
        if (cartStore.refresh) {
          await cartStore.refresh();
        }
      }
    } catch (error) {
      console.error('Error refreshing cart UI:', error);
    }
  }

  // Setup cart monitoring
  setupCartListener() {
    // Listen for cart update events from the theme
    document.addEventListener(ThemeEvents.cartUpdate, () => {
      this.debouncedCartCheck();
    });

    // Listen for clicks on remove buttons and quantity changes as additional triggers
    document.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        // Check if it's a cart item remove button or quantity change
        if (
          target.closest(
            '[data-cart-item-remove], .cart-item-remove, [aria-label*="remove"], button[data-action="remove"]'
          ) ||
          target.closest('quantity-input, [data-quantity-input]')
        ) {
          // Wait a bit for the cart to update, then check
          setTimeout(() => {
            this.debouncedCartCheck();
          }, 500);
        }
      },
      true
    ); // Use capture phase to catch events early
  }

  // Debounced cart check to avoid excessive API calls
  debouncedCartCheck() {
    if (this.cartCheckTimeout) {
      clearTimeout(this.cartCheckTimeout);
    }

    this.cartCheckTimeout = setTimeout(async () => {
      try {
        if (this.isProcessing) return;

        const cart = await this.getCurrentCart();
        await this.handleGiftProduct(cart);
      } catch (error) {
        console.error('Error in debounced cart check:', error);
        this.isProcessing = false; // Reset flag on error
      }
    }, 300); // Wait 300ms after last event before checking
  }

  // Handle gift product
  async handleGiftProduct(cart) {
    if (!cart?.items?.length) {
      // If cart is empty, remove gift if it exists and clear the received flag
      const existingGift = cart.items?.find(
        (item) => item.variant_id === CONFIG.freeGiftVariantId && item.properties?._klaviyo_subscriber_gift === 'true'
      );
      if (existingGift) {
        await this.removeFreeGift();
      } else {
        // Clear the received flag since cart is empty and no gift exists
        localStorage.removeItem(CONFIG.cartStorageKey);
      }
      return;
    }

    // Only process gifts for customers who have subscribed via Klaviyo
    if (!this.isCustomerEligible()) {
      //console.log('🚫 Customer not eligible for free gift - no Klaviyo subscription');
      // If customer is not eligible but gift exists, remove it
      const existingGift = cart.items.find(
        (item) => item.variant_id === CONFIG.freeGiftVariantId && item.properties?._klaviyo_subscriber_gift === 'true'
      );
      if (existingGift) {
        await this.removeFreeGift();
        localStorage.removeItem(CONFIG.cartStorageKey);
      }
      return;
    }

    const now = Date.now();
    if (this.isProcessing || now - this.lastGiftCheck < 500) return;

    this.lastGiftCheck = now;
    this.isProcessing = true;

    try {
      const cartSubtotal = this.calculateCartTotalExcludingGiftCards(cart);
      // Check for Klaviyo subscriber gift specifically
      const existingGift = cart.items.find(
        (item) => item.variant_id === CONFIG.freeGiftVariantId && item.properties?._klaviyo_subscriber_gift === 'true'
      );
      const hasGift = !!existingGift;

      // Ensure quantity is 1 if gift exists
      if (hasGift && existingGift.quantity !== 1) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: existingGift.key,
            quantity: 1,
          }),
        });
        this.markGiftAsReceived();
        await this.refreshCartUI();
        // Continue to check if cart still qualifies after fixing quantity
      }

      /* console.log('🎁 Free Gift Check (Eligible Customer):', 
      {
        cartSubtotal,
        threshold: CONFIG.cartThreshold,
        hasGift,
        giftVariantId: CONFIG.freeGiftVariantId
      });*/

      // Always check if cart qualifies, regardless of hasReceivedGift status
      if (cartSubtotal >= CONFIG.cartThreshold && !hasGift) {
        //console.log('✅ Adding free gift - threshold met');
        await this.addFreeGift();
      } else if (cartSubtotal < CONFIG.cartThreshold && hasGift) {
        //console.log('❌ Removing free gift - threshold not met');
        await this.removeFreeGift();
        // Clear the received flag when gift is removed due to threshold
        localStorage.removeItem(CONFIG.cartStorageKey);
      } else if (cartSubtotal >= CONFIG.cartThreshold && hasGift) {
        // Cart qualifies and gift exists - ensure it's marked as received
        this.markGiftAsReceived();
        //console.log('✅ Cart qualifies and gift is present - no action needed');
      } else {
        //console.log('⏸️ No action needed');
      }
    } catch (error) {
      console.error('Error in handleGiftProduct:', error);
    } finally {
      this.isProcessing = false;
    }
  }

  // Calculate cart total excluding gift cards
  calculateCartTotalExcludingGiftCards(cart) {
    if (!cart?.items?.length) return 0;

    // Get excluded product IDs from config (convert to strings for comparison)
    const excludedProductIds = CONFIG.excludedProductIds.map((id) => id.toString());

    return cart.items.reduce((total, item) => {
      // Exclude products that are in the excluded list
      const isExcludedProduct = excludedProductIds.includes(item.product_id.toString());
      // Exclude items marked as free gifts
      const isFreeGift = item.properties?._free_gift === 'true';

      if (isExcludedProduct || isFreeGift) {
        return total;
      }
      return total + item.final_line_price;
    }, 0);
  }

  // Check if customer is eligible for gift
  isCustomerEligible() {
    const subscriptionData = localStorage.getItem(CONFIG.storageKey);
    if (!subscriptionData) return false;

    try {
      const data = JSON.parse(subscriptionData);
      return data.eligible === true;
    } catch {
      return false;
    }
  }

  // Check initial state on page load
  async checkInitialState() {
    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));

      if (this.isCustomerEligible()) {
        //console.log('✅ Customer is eligible for free gift - checking initial state');
        const cart = await this.getCurrentCart();
        if (cart?.items?.length) {
          await this.handleGiftProduct(cart);
        }
      } else {
        //console.log('🚫 Customer not eligible for free gift - skipping initial check');
      }
    } catch (error) {
      console.error('Error checking initial state:', error);
    }
  }

  // Cleanup
  destroy() {
    if (this.cartCheckTimeout) {
      clearTimeout(this.cartCheckTimeout);
    }
  }
}

// Initialize the free gift manager
let freeGiftManager = null;

document.addEventListener('DOMContentLoaded', () => {
  try {
    freeGiftManager = new FreeGiftManager();
  } catch (error) {
    console.error('Error initializing FreeGiftManager:', error);
  }
});

// Cleanup on page unload
window.addEventListener('beforeunload', () => {
  if (freeGiftManager) {
    freeGiftManager.destroy();
  }
});
